import {Component, ViewEncapsulation, OnInit, OnDestroy, ElementRef, ViewChild} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {FormBuilder, FormGroup, Validators, FormControl} from "@angular/forms";
import {SearchRequest} from "@portal/api/requests/search-request";
import {BrandsService} from '@portal/api/services/brands.service';
import {EcuProducersService} from "@portal/api/services/ecu-producers.service";
import {EcuTypesService} from "@portal/api/services/ecu-types.service";
import {StorageService} from "@portal/shared/services/storage.service";
import {Subject, takeUntil, debounceTime, distinctUntilChanged} from 'rxjs';

@Component({
    selector: 'app-homepage',
    templateUrl: './homepage.component.html',
    styleUrls: ['./homepage.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class HomepageComponent implements OnInit, OnDestroy {

    constructor(private route: ActivatedRoute,
                protected fb: FormBuilder,
                private _router: Router,
                private _ecuProducerService: EcuProducersService,
                private _ecuTypesService: EcuTypesService,
                private _brandsService: BrandsService,
                private _storageService: StorageService) {
    }

    isLoadingSpinnerActive: boolean;
    form: FormGroup;
    innerPage: boolean;
    ecuProducerOptions: { label: string, value: string }[] = []
    brandOptions: { label: string, value: string }[] = []
    ecuTypeOptions: { label: string, value: string }[] = []
    
    // Search controls
    brandSearchControl = new FormControl('');
    ecuProducerSearchControl = new FormControl('');
    ecuTypeSearchControl = new FormControl('');
    
    // Filtered options
    filteredBrandOptions: { label: string, value: string }[] = []
    filteredEcuProducerOptions: { label: string, value: string }[] = []
    filteredEcuTypeOptions: { label: string, value: string }[] = []
    
    private destroy$ = new Subject<void>();

    ngOnInit() {
        this.route.queryParams.subscribe(params => {
            let page = params['p'];
            if (page === 'inner') {
                this.innerPage = true;
            }
        });
        this.form = this.fb.group({
            ecuTypeId: [null, [Validators.required]],
            brandId: [null, [Validators.required]],
            ecuProducerId: [null, [Validators.required]],
        });

        this._brandsService.list({
            getAll: true
        } as SearchRequest).subscribe(brands => {
            // order by name
            brands.items.sort((a, b) => a.name.localeCompare(b.name));
            this.brandOptions = brands.items.map(brand => {
                return {
                    label: brand.name,
                    value: brand.id.toString()
                };
            });
            this.filteredBrandOptions = [...this.brandOptions];
        });

        this._ecuProducerService.list({
            getAll: true
        } as SearchRequest).subscribe(ecuProducers => {
            // order by name
            ecuProducers.items.sort((a, b) => a.name.localeCompare(b.name));
            this.ecuProducerOptions = ecuProducers.items.map(ecuProducer => {
                return {
                    label: ecuProducer.name,
                    value: ecuProducer.id.toString()
                };
            });
            this.filteredEcuProducerOptions = [...this.ecuProducerOptions];
        });
        
        // Set up search listeners
        this.setupSearchListeners();
    }
    
    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }
    
    onBrandSelectOpened(opened: boolean): void {
        if (opened) {
            // Add search input immediately to prevent flash
            this.addSearchInputToPanel('.brand-select-panel', this.brandSearchControl, 'Search brands...');
        }
    }
    
    onEcuProducerSelectOpened(opened: boolean): void {
        if (opened) {
            // Add search input immediately to prevent flash
            this.addSearchInputToPanel('.ecu-producer-select-panel', this.ecuProducerSearchControl, 'Search ECU brands...');
        }
    }
    
    onEcuTypeSelectOpened(opened: boolean): void {
        if (opened) {
            // Add search input immediately to prevent flash
            this.addSearchInputToPanel('.ecu-type-select-panel', this.ecuTypeSearchControl, 'Search ECU models...');
        }
    }
    
    private addSearchInputToPanel(panelSelector: string, control: FormControl, placeholder: string): void {
        // Use requestAnimationFrame for better timing
        requestAnimationFrame(() => {
            const panel = document.querySelector(panelSelector);
            if (panel) {
                // Remove existing search input if any
                const existingSearch = panel.querySelector('.search-input');
                if (existingSearch) {
                    existingSearch.remove();
                }
                
                // Create search input
                const searchInput = document.createElement('input');
                searchInput.className = 'search-input';
                searchInput.placeholder = placeholder;
                searchInput.style.cssText = `
                    width: calc(100% - 16px);
                    padding: 8px 12px;
                    border: 1px solid #ccc;
                    border-radius: 4px;
                    margin: 8px;
                    font-size: 14px;
                    outline: none;
                    background: white;
                    box-sizing: border-box;
                    opacity: 0;
                    transition: opacity 0.2s ease-in-out;
                `;
                
                // Add event listener
                searchInput.addEventListener('input', (event: any) => {
                    const searchText = event.target.value;
                    control.setValue(searchText);
                });
                
                // Insert at the top of the panel
                panel.insertBefore(searchInput, panel.firstChild);
                
                // Focus and fade in
                setTimeout(() => {
                    searchInput.focus();
                    searchInput.style.opacity = '1';
                }, 50);
            }
        });
    }
    
    private setupSearchListeners(): void {
        // Brand search
        this.brandSearchControl.valueChanges
            .pipe(
                takeUntil(this.destroy$),
                debounceTime(300),
                distinctUntilChanged()
            )
            .subscribe(searchText => {
                this.filterBrands(searchText || '');
            });
            
        // ECU Producer search
        this.ecuProducerSearchControl.valueChanges
            .pipe(
                takeUntil(this.destroy$),
                debounceTime(300),
                distinctUntilChanged()
            )
            .subscribe(searchText => {
                this.filterEcuProducers(searchText || '');
            });
            
        // ECU Type search
        this.ecuTypeSearchControl.valueChanges
            .pipe(
                takeUntil(this.destroy$),
                debounceTime(300),
                distinctUntilChanged()
            )
            .subscribe(searchText => {
                this.filterEcuTypes(searchText || '');
            });
    }
    
    private filterBrands(searchText: string): void {
        if (!searchText.trim()) {
            this.filteredBrandOptions = [...this.brandOptions];
        } else {
            this.filteredBrandOptions = this.brandOptions.filter(option =>
                option.label.toLowerCase().includes(searchText.toLowerCase())
            );
        }
    }
    
    private filterEcuProducers(searchText: string): void {
        if (!searchText.trim()) {
            this.filteredEcuProducerOptions = [...this.ecuProducerOptions];
        } else {
            this.filteredEcuProducerOptions = this.ecuProducerOptions.filter(option =>
                option.label.toLowerCase().includes(searchText.toLowerCase())
            );
        }
    }
    
    private filterEcuTypes(searchText: string): void {
        if (!searchText.trim()) {
            this.filteredEcuTypeOptions = [...this.ecuTypeOptions];
        } else {
            this.filteredEcuTypeOptions = this.ecuTypeOptions.filter(option =>
                option.label.toLowerCase().includes(searchText.toLowerCase())
            );
        }
    }

    ecuProducerChange(value: any) {
        this._ecuTypesService.list({
            getAll: true,
            filters: [{code: 'ecuproducerid', values: [value.value]}]
        } as SearchRequest).subscribe(ecuTypes => {
            // order by name
            ecuTypes.items.sort((a, b) => a.name.localeCompare(b.name));
            this.ecuTypeOptions = ecuTypes.items.map(ecuType => {
                return {
                    label: ecuType.name,
                    value: ecuType.id.toString()
                };
            });
            this.filteredEcuTypeOptions = [...this.ecuTypeOptions];
        });
    }

    search() {
        this.isLoadingSpinnerActive = true;
        if (this.form.valid) {
            this.isLoadingSpinnerActive = false;
            this._storageService.addCurrentSearchData(this.form.value);
            this._storageService.addCurrentSearchNameData({
                brand: this.brandOptions.find(x => x.value === this.form.get('brandId').value).label,
                ecuProducer: this.ecuProducerOptions.find(x => x.value === this.form.get('ecuProducerId').value).label,
                ecuType: this.ecuTypeOptions.find(x => x.value === this.form.get('ecuTypeId').value).label
            });
            this._router.navigate(['/search']);
        }
    }
}
